/**
 * Animated hover/selection scale for node geometry.
 *
 * Nodes.tsx inflates a node's sphere when you hover (1.28x) or select it
 * (1.45x). That used to be applied as a hard step, which was fine while
 * nothing else was anchored to the sphere's edge — the pop read as part
 * of the click feedback.
 *
 * Once labels started sizing themselves off the node's REAL radius, the
 * step became visible as a separate defect: selecting a node teleported
 * its label upward. Easing the boost fixes the label (it now glides out
 * as the sphere swells, which reads as the sphere pushing it) and makes
 * the sphere itself feel less abrupt.
 *
 * State lives here rather than in Nodes.tsx because the labels and the
 * bridge rings have to read the SAME animated value — if any of them
 * animated on its own curve they'd visibly drift apart mid-transition.
 * It's a plain mutable module singleton, read per-frame from useFrame,
 * so nothing here triggers a React render.
 *
 * The map only ever holds the handful of nodes that aren't at rest;
 * entries are dropped once they settle back to 1.
 */
import { HOVERED_BOOST, SELECTED_BOOST } from './sizing';

/**
 * Exponential time constant, in seconds. ~60ms is fast enough that
 * hovering still feels instant (hover fires constantly during a scan, so
 * a slow curve here would read as lag) while removing the hard step.
 */
const RESPONSE_TAU = 0.06;
/** Below this distance from 1 a decaying node is treated as settled. */
const SETTLED_EPSILON = 0.002;

const scales = new Map<string, number>();

/**
 * Advance every in-flight boost one frame. Call ONCE per frame, from
 * Nodes.tsx — everything else is a read-only consumer.
 */
export function advanceBoosts(
  dt: number,
  hoverId: string | null,
  selectedId: string | null,
): void {
  // Frame-rate independent exponential approach: the same visual curve
  // whether we're running at 30 or 144fps. Clamped so a long stall (tab
  // backgrounded, GC pause) doesn't overshoot.
  const k = 1 - Math.exp(-Math.min(0.1, dt) / RESPONSE_TAU);

  // Make sure newly-targeted nodes are in the map, starting from rest.
  if (hoverId && !scales.has(hoverId)) scales.set(hoverId, 1);
  if (selectedId && !scales.has(selectedId)) scales.set(selectedId, 1);

  for (const [id, cur] of scales) {
    const target =
      id === selectedId
        ? SELECTED_BOOST
        : id === hoverId
          ? HOVERED_BOOST
          : 1;
    const next = cur + (target - cur) * k;
    // A node relaxing back to rest gets dropped once it's close enough,
    // so the map stays tiny (at most the hovered + selected node, plus
    // whatever is still decaying).
    if (target === 1 && Math.abs(next - 1) < SETTLED_EPSILON) {
      scales.delete(id);
    } else {
      scales.set(id, next);
    }
  }
}

/** Current animated scale for a node. 1 when it's at rest. */
export function boostOf(id: string): number {
  return scales.get(id) ?? 1;
}
